import { Link } from "react-router-dom";
import PublishedMethodology from "../../components/PublishedMethodology";

/*
 * Blog article: "Your Bloodwork Graded Against the Optimized, Not the Average"
 * Rendered inside BlogPost.jsx via <article className="legal-prose">.
 */
export default function ArticleBody() {
    return (
        <>
            <h2>Quick answer</h2>
            <p>
                The Overall Score is the single number the{" "}
                <Link to="/blog/how-trt-plus-analyzes-your-bloodwork">
                    TRT Plus engine
                </Link>{" "}
                produces from your bloodwork: a grade from 0 to 1000,
                measured against optimized TRT targets instead of the
                general population's. The engine weighs fifteen markers,
                reads them together in context, ratios included, and
                distills them into one figure. A perfect panel scores 1000,
                and every marker outside its target range pulls the score
                down.
            </p>

            <h2>The panel isn't wrong. The benchmark is.</h2>
            <p>
                Standard lab reference ranges are built from the general
                population, and the general population is not well. Those
                ranges are the average of a group that is, in large part,
                overweight, insulin-resistant, and inflamed. Score yourself
                against that and "normal" only means "typical." It doesn't
                mean good, and it's not the bloodwork of a peak male.
            </p>
            <p>
                For a man on testosterone, that benchmark is worse than
                unhelpful. Your total testosterone reads high against a
                population that isn't jacked. Your hematocrit may sit above
                the untreated average. On a standard panel you look
                abnormal, because the panel is comparing you to people who
                aren't doing what you're doing.
            </p>
            <p>
                The engine throws that benchmark out. It grades each marker
                against an optimized, TRT-calibrated target, so the number
                reflects how well your protocol is actually working, not
                how far you've drifted from a sick average.
            </p>

            <h2>Four tiers, not one "normal"</h2>
            <p>
                The core move is this: every marker is scored against one
                of four tiers, not a single population range.
            </p>
            <ul>
                <li>
                    <strong>Optimal.</strong> A health-maximizing target,
                    where lower or well-controlled is unambiguously better.
                </li>
                <li>
                    <strong>TRT Calibrated.</strong> The dialed-in
                    operating range for someone on TRT. At or above
                    standard lab ranges by design, and not a risk driver
                    in itself.
                </li>
                <li>
                    <strong>Enhanced.</strong> Supraphysiologic territory,
                    reachable only with enhancement protocols. A
                    performance choice with a tradeoff, not a health
                    optimum.
                </li>
                <li>
                    <strong>Tolerated.</strong> A value that carries some
                    intrinsic risk, accepted and monitored in context
                    because your surrounding markers are low-risk.
                    Monitored, not risk-free.
                </li>
            </ul>

            <h2>How the number is built</h2>
            <p>
                Everyone starts at 1,000. Each of fifteen inputs is
                compared to its target band. A marker inside its band costs
                nothing. A marker outside its band subtracts points, scaled
                by two things: how far past the edge it sits, and how
                heavily that marker is weighted. The penalties are summed
                and subtracted from 1,000.
            </p>
            <p>
                So a lightly weighted marker sitting just past its edge
                costs a few points. A heavily weighted marker sitting well
                outside its band costs a lot. Two markers slightly out can
                cost less than a single marker far out on a heavy weight.
            </p>
            <p>
                It's deterministic: no randomness, no black box. The same
                bloodwork always produces the same score, every time you
                open it. Where you land:
            </p>
            <ul>
                <li>
                    <strong>1000:</strong> Perfect
                </li>
                <li>
                    <strong>800–999:</strong> Excellent
                </li>
                <li>
                    <strong>650–799:</strong> OK
                </li>
                <li>
                    <strong>500–649:</strong> Not Great
                </li>
                <li>
                    <strong>250–499:</strong> Needs Attention
                </li>
                <li>
                    <strong>Below 250:</strong> Terrible
                </li>
            </ul>

            <figure className="my-12 flex flex-col items-center">
                <div className="relative w-full max-w-[300px]">
                    <div className="phone-frame">
                        <div className="phone-screen">
                            <img
                                src="/blog/my-biomarkers-dashboard.webp"
                                alt="TRT Plus My Biomarkers dashboard on iPhone — Overall Score 989 with fifteen biomarkers laid out in a grid (IGF-1, Total T, E2, Hematocrit, BP, ApoB, Body Fat, TSH, CRP, SHBG, DHT, PSA, HbA1c, GGT, Cystatin C), plus the T:SHBG and A:E hormone ratio gauges."
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover object-top"
                            />
                        </div>
                    </div>
                </div>
                <figcaption className="mt-6 text-sm text-white/50 italic text-center">
                    Overall Score 989 — Excellent tier — built from fifteen biomarkers and two hormone ratios, each read in context of the others.
                </figcaption>
            </figure>

            <h2>It reads markers in context, not in isolation</h2>
            <p>
                The score isn't fifteen separate grades bolted together.
                Several bands move based on your other markers, because a
                number that's fine in one context is a problem in another.
            </p>
            <p>
                Your <strong>testosterone</strong> band extends when your
                cardiovascular markers are clean, and tightens when blood
                pressure, ApoB, body fat, or HbA1c climb. High testosterone
                is better tolerated in an otherwise pristine metabolic
                picture, so the target reflects that. That's the
                Cardio-Metabolic Cascade.
            </p>
            <p>
                Your <strong>IGF-1</strong> target scales with your
                glycemic control. Good long-term glucose control gives you
                room to run IGF-1 higher; as control slips, the target
                eases down. That's the{" "}
                <Link to="/blog/the-growth-axis">Growth Axis</Link>.
            </p>
            <p>
                Your <strong>hematocrit</strong> ceiling is context-adjusted
                to your broader cardiovascular risk rather than read off a
                fixed line.
            </p>
            <p>
                These adjustments change the range in context. They don't
                erase the risk a marker carries on its own.
            </p>

            <h2>What's actually in the score</h2>
            <p>
                Fifteen markers, each mapped to a real health outcome,
                grouped by what they watch:
            </p>
            <ul>
                <li>
                    <strong>Hormonal:</strong> Total Testosterone,
                    Estradiol, DHT, SHBG, and IGF-1, covering androgen
                    status, body composition, and the growth and prostate
                    signals.
                </li>
                <li>
                    <strong>Cardiometabolic:</strong> Blood Pressure, ApoB
                    (or LDL), CRP, HbA1c, Hematocrit, and Body Fat,
                    covering the atherosclerosis, inflammation, glucose,
                    and clotting risks that matter most on TRT.
                </li>
                <li>
                    <strong>Systemic:</strong> TSH, PSA, GGT, and Cystatin
                    C (or eGFR), covering thyroid, prostate, liver, and
                    kidney.
                </li>
            </ul>
            <p>
                Every one is tied to a peer-reviewed outcome, from
                atherogenic particle count to all-cause mortality. The
                point isn't more numbers. It's the right fifteen, weighted
                the way they actually matter for a man on TRT.
            </p>

            <h2>The method behind it</h2>
            <p>
                Distilling many markers into one index isn't new. It's an
                established approach in preventive-health and aging
                research, the lineage behind biological-age models (
                <a
                    href="https://doi.org/10.1016/j.mad.2005.10.004"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Klemera &amp; Doubal, 2006
                </a>
                ;{" "}
                <a
                    href="https://doi.org/10.1371/journal.pmed.1002718"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Liu et al., 2018
                </a>
                ) and the American Heart Association's Life's Essential 8
                (
                <a
                    href="https://doi.org/10.1161/CIR.0000000000001078"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Lloyd-Jones et al., 2022
                </a>
                ): one number built from many inputs.
            </p>
            <p>
                The TRT Plus engine is our own implementation of that
                approach. Which markers are included, how they're
                weighted, and where the target bands sit were developed
                with TRT clinics, coaches, and pathology partners, and
                calibrated for men on testosterone therapy.
            </p>
            <p>
                It has not been validated against health outcomes in a
                formal cohort study, and it isn't a diagnostic. It's a
                decision-support instrument, built to be read alongside
                your healthcare provider, not instead of one.
            </p>

            <div className="hairline my-10" />

            <p className="text-white/50 text-sm italic">
                This article is for informational purposes only and does not
                constitute medical advice. Consult with your healthcare
                provider before making medical decisions.
            </p>

            <h3>Read the methodology</h3>
            <p>
                Every model, weighting, and target band behind the score is
                documented, with the schematic and formulas, on the engine
                page. <Link to="/engine">See the engine</Link> ·{" "}
                <Link to="/">Get TRT Plus</Link>.
            </p>

            <h3>Related reading</h3>
            <ul>
                <li>
                    <Link to="/blog/how-trt-plus-analyzes-your-bloodwork">
                        How TRT Plus Analyzes Your Bloodwork: A
                        Deterministic Engine, Then AI
                    </Link>
                </li>
                <li>
                    <Link to="/blog/the-growth-axis">
                        The Growth Axis: Why Your IGF-1 Range Scales With
                        Your Health
                    </Link>
                </li>
            </ul>

            <div className="mt-14">
                <PublishedMethodology
                    slugs={["overall-score", "cascade", "androgen-load"]}
                    useFullTitle={true}
                />
            </div>
        </>
    );
}
